/**
 * Copyright (c) 2018 itemis AG - All rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 *
 * Contributors:
 * 	andreas muelder - itemis AG
 *
 */
import * as ace from "ace-builds";
import ReconnectingWebSocket from "reconnecting-websocket";
import { MessageEventBus, UIEventBus } from "../../event-bus";
import "ace-builds/src-noconflict/ext-language_tools";
import { DARK_MODE_THEME, LIGHT_MODE_THEME } from "../language-server/ace-factory";
import { aceAnnotationToTextDiagnostic } from "../services/ui/text-diagnostic";
import { getConfig } from "../../../config";

const langTools = ace.require("ace/ext/language_tools");

/* patch auto completion to only filter named elements */
const aceAutoComplete = ace.require("ace/autocomplete");
const aceAutoCompleteUtil = ace.require("ace/autocomplete/util");

const filterCompletionsOld = aceAutoComplete.FilteredList.prototype.filterCompletions;
aceAutoComplete.FilteredList.prototype.filterCompletions = function (items, needle) {
  const isIdentifierItem = (item) => {
    const id = aceAutoCompleteUtil.retrievePrecedingIdentifier(item.value, item.value.length);
    return id.length === item.value.length && id.charAt(0) !== "-";
  };
  const identifierItems = items.filter((i) => isIdentifierItem(i));
  const nonIdentifierItems = items.filter((i) => !isIdentifierItem(i));
  nonIdentifierItems.forEach((i) => {
    i.$score = 1;
    i.completer = {
      insertMatch: function (editor, data) {
        editor.execCommand("insertstring", data.value || data);
      },
    };
  });
  const identifierItemsFiltered = filterCompletionsOld.call(
    aceAutoComplete.FilteredList.prototype,
    identifierItems,
    needle
  );
  return identifierItemsFiltered.concat(nonIdentifierItems);
};
/* patch end */

export default class AceLanguageServer {
  editor: ace.Ace.Editor | null = null;
  socket: ReconnectingWebSocket;
  extension: string;
  private static INSTANCE: AceLanguageServer;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  callback: (event: any) => void;
  private connectionFailed = false;
  private pendingRequests: Map<number, (msg: MessageEvent) => void> = new Map();
  private nextRequestId = 1;
  private didChangeTimeout: ReturnType<typeof setTimeout> | null = null;
  private static DEBOUNCE_MS = 200;

  private constructor(url: string) {
    this.start(url);
  }

  private start(url: string) {
    this.socket = new ReconnectingWebSocket(url);

    this.socket.onerror = () => {
      if (this.connectionFailed) {
        return;
      }
      this.connectionFailed = true;
      MessageEventBus.emit("error", {
        id: "ace-lsp-adapter",
        text: "Unable to connect to language server.",
      });
    };

    this.socket.onopen = () => {
      if (this.connectionFailed) {
        MessageEventBus.emit("information", {
          id: "ace-lsp-adapter",
          text: "Connected to language server.",
        });
      }
      this.connectionFailed = false;
    };

    this.socket.onmessage = (msg) => this.handleMessage(msg);
  }

  private sendToServer(message: object) {
    // console.debug("[LSP →]", message);
    this.socket.send(JSON.stringify(message));
  }

  private handleMessage(msg: MessageEvent) {
    const data = JSON.parse(msg.data);
    // console.debug("[LSP ←]", data);

    // LSP response to a request (has an id)
    if (data.id !== undefined) {
      const callback = this.pendingRequests.get(data.id);
      if (callback) {
        this.pendingRequests.delete(data.id);
        callback(msg);
      }
      return;
    }

    // LSP notification (has method, no id) — e.g. textDocument/publishDiagnostics
    if (data.method === "textDocument/publishDiagnostics" && this.editor) {
      this.validationCallback(this.editor, msg);
      return;
    }
  }

  public restart() {
    const { languageServerURL } = getConfig();
    this.socket.close();
    this.connectionFailed = false;
    this.start(languageServerURL);
  }

  public static getInstance() {
    const { languageServerURL } = getConfig();
    if (!AceLanguageServer.INSTANCE) {
      AceLanguageServer.INSTANCE = new AceLanguageServer(languageServerURL);
    }
    return AceLanguageServer.INSTANCE;
  }

  connect(editor: ace.Ace.Editor, extension: string) {
    if (this.editor !== null) this.disconnect(this.editor);
    this.editor = editor;
    this.extension = extension;
    this.didOpen(editor);
    editor.on(
      "change",
      (this.callback = (delta: ace.Ace.Delta) => {
        this.didChange(editor, delta);
      })
    );
    const flowCompleter = {
      getCompletions: (
        editor: ace.Ace.Editor,
        session: ace.Ace.EditSession,
        /* eslint-disable @typescript-eslint/no-explicit-any*/
        pos: any,
        prefix: any,
        callback: any
        /* eslint-enable @typescript-eslint/no-explicit-any*/
      ) => {
        this.completions(editor, callback);
      },
      getDocTooltip: function (item) {
        if (item.docHTML && editor.getTheme() === DARK_MODE_THEME && !item.docHTML.includes("ace_tooltip_dark")) {
          item.docHTML = `
                    <div class="ace_tooltip_dark">
                    ${item.docHTML}
                    </div>
                    `;
        } else if (
          item.docHTML &&
          editor.getTheme() === LIGHT_MODE_THEME &&
          !item.docHTML.includes("ace_tooltip_light")
        ) {
          item.docHTML = `
                    <div class="ace_tooltip_light">
                    ${item.docHTML}
                    </div>
                    `;
        }
      },
    };

    langTools.setCompleters([flowCompleter]);
  }

  disconnect(editor: ace.Ace.Editor) {
    if (this.editor === null) {
      return;
    }
    this.markerIds.forEach((id) => editor.getSession().removeMarker(id));
    langTools.setCompleters([]);
    editor.off("change", this.callback);
    this.editor = null;
  }

  didOpen(editor: ace.Ace.Editor) {
    this.sendToServer({
      jsonrpc: "2.0",
      method: "textDocument/didOpen",
      params: {
        textDocument: {
          uri: "inmemory://yakindu/file." + this.extension,
          version: 1,
          text: editor.getValue(),
        },
      },
    });
  }

  didChange(editor: ace.Ace.Editor, _delta: ace.Ace.Delta) {
    if (this.didChangeTimeout !== null) {
      clearTimeout(this.didChangeTimeout);
    }
    this.didChangeTimeout = setTimeout(() => {
      this.didChangeTimeout = null;
      this.sendDidChange(editor);
    }, AceLanguageServer.DEBOUNCE_MS);
  }

  private flushDidChange(editor: ace.Ace.Editor) {
    if (this.didChangeTimeout !== null) {
      clearTimeout(this.didChangeTimeout);
      this.didChangeTimeout = null;
      this.sendDidChange(editor);
    }
  }

  private sendDidChange(editor: ace.Ace.Editor) {
    this.sendToServer({
      jsonrpc: "2.0",
      method: "textDocument/didChange",
      params: {
        textDocument: {
          uri: "inmemory://yakindu/file." + this.extension,
        },
        contentChanges: [
          {
            text: editor.getValue(),
          },
        ],
      },
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  completions(editor: ace.Ace.Editor, callback: () => {}) {
    this.flushDidChange(editor);
    const requestId = this.nextRequestId++;
    this.pendingRequests.set(requestId, (msg) => this.completionsCallback(msg, callback));
    const completionRequest = {
      jsonrpc: "2.0",
      id: requestId,
      method: "textDocument/completion",
      params: {
        textDocument: {
          uri: "inmemory://yakindu/file." + this.extension,
        },
        position: {
          line: editor.getCursorPosition().row,
          character: editor.getCursorPosition().column,
        },
        context: {
          triggerKind: 1,
        },
      },
    };
    this.sendToServer(completionRequest);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  completionsCallback(msg: MessageEvent, callback: any) {
    const completions = JSON.parse(msg.data).result?.items?.map((i) => {
      return {
        name: i.label,
        value: i.label,
        score: 100,
        meta: "",
        docHTML: i.documentation,
      };
    });
    callback(null, completions);
  }

  markerIds: Array<number> = [];

  validationCallback(editor: ace.Ace.Editor, msg: MessageEvent) {
    const annotations: ace.Ace.Annotation[] = [];
    this.markerIds.forEach((id) => editor.getSession().removeMarker(id));
    this.markerIds = [];
    const data = JSON.parse(msg.data);
    if (!data.params?.uri.endsWith(this.extension)) {
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data.params?.diagnostics?.forEach((issue: any) => {
      //Annotations
      const severity = issue.severity;
      annotations.push({
        row: issue.range.start.line,
        column: issue.range.start.character,
        text: issue.message,
        type: severity === 1 ? "error" : severity === 2 ? "warning" : "information",
      });
      //Markers
      this.markerIds.push(
        editor.session.addMarker(
          new ace.Range(
            issue.range.start.line,
            issue.range.start.character,
            issue.range.end.line,
            issue.range.end.character
          ),
          severity === 1 ? "ace_error_marker" : "ace_warning_marker",
          "text",
          true
        )
      );
    });
    editor.getSession().setAnnotations(annotations);
    UIEventBus.emit("editorValidated", aceAnnotationToTextDiagnostic(annotations));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  gotoDefinition(editor: ace.Ace.Editor, data: any) {
    this.flushDidChange(editor);
    const requestId = this.nextRequestId++;
    this.pendingRequests.set(requestId, (msg) => this.gotoDefinitionCallback(editor, msg));
    const gotoDefinitionRequest = {
      jsonrpc: "2.0",
      id: requestId,
      method: "textDocument/definition",
      params: {
        textDocument: {
          uri: "inmemory://yakindu/file." + this.extension,
        },
        position: {
          line: data.position.row,
          character: data.position.column,
        },
      },
    };
    this.sendToServer(gotoDefinitionRequest);
  }

  gotoDefinitionCallback(editor: ace.Ace.Editor, msg: MessageEvent) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    JSON.parse(msg.data).result?.forEach((item: any) => {
      const range = new ace.Range(
        item.range.start.line,
        item.range.start.character,
        item.range.end.line,
        item.range.end.character
      );
      editor.selection.setRange(range, false);
    });
  }
}
